import { useRef, useState, useEffect } from "react";
import { tr } from "../i18n/tr";
import { Eraser, Check, X } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Reusable signature canvas. Call ``onConfirm(dataUrl)`` on save.
 * Accepts a ``height`` prop (default 140px) and a full-width canvas.
 */
export default function SignaturePad({
  onConfirm,
  onCancel,
  height = 140,
  label = tr("lms.signez_ici_86e2", "Signez ici"),
  confirmLabel = tr("lms.valider_ma_signature_0f9e", "Valider ma signature"),
  hint = tr("lms.dessinez_votre_signature_elle_3870", "Dessinez votre signature. Elle sera datee et horodatee avec consentement eIDAS."),
  defaultName = "",
}) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [consent, setConsent] = useState(false);
  const [name, setName] = useState(defaultName || "");

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    c.width = c.offsetWidth * ratio;
    c.height = height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2.2;
    ctx.fillStyle = "#0b0811";
    ctx.fillRect(0, 0, c.offsetWidth, height);
  }, [height]);

  const _pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t?.clientX ?? e.clientX) - r.left, y: (t?.clientY ?? e.clientY) - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = _pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = _pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0b0811";
    ctx.fillRect(0, 0, c.offsetWidth, height);
    setHasInk(false);
  };

  const submit = () => {
    if (!hasInk) return;
    const data = canvasRef.current.toDataURL("image/png");
    onConfirm && onConfirm({ signature_b64: data, signed_name: name, consent });
  };

  return (
    <div className="space-y-3" data-testid="signature-pad">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 font-bold">{label}</div>
        <canvas
          ref={canvasRef}
          className="w-full rounded-lg border border-white/10 bg-[#0b0811] touch-none cursor-crosshair"
          style={{ height: height + "px" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="signature-canvas"
        />
      </div>

      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={tr("lms.nom_et_prenom_pour_48ad", "Nom et prenom pour signer")}
        className="w-full bg-[#14101c] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-[var(--brand-primary,#f7c935)] outline-none"
        data-testid="signature-name-input"
      />

      <label className="flex items-start gap-2 text-[11px] text-slate-400 leading-relaxed cursor-pointer">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="signature-consent-checkbox" />
        <span>J'atteste que je suis la personne nommee ci-dessus, je consens a signer ce document electroniquement (eIDAS — niveau simple) et je reconnais avoir pris connaissance de son contenu.</span>
      </label>

      <p className="text-[10px] text-slate-500">{hint}</p>

      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" onClick={clear} className="border border-white/10 text-xs" data-testid="signature-clear-btn">
          <Eraser className="w-3.5 h-3.5 mr-1" /> {tr("lms.effacer_1e9a", "Effacer")}
        </Button>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="ghost" onClick={onCancel} className="text-xs text-slate-400" data-testid="signature-cancel-btn">
              <X className="w-3.5 h-3.5 mr-1" /> {tr("lms.annuler_d12c", "Annuler")}
            </Button>
          )}
          <Button
            disabled={!hasInk || !consent || !name.trim()}
            onClick={submit}
            className="bg-[var(--brand-primary,#f7c935)] text-[#14101c] font-bold text-xs disabled:opacity-50"
            data-testid="signature-submit-btn"
          >
            <Check className="w-3.5 h-3.5 mr-1" /> {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
